import React, { useState } from "react";
import { Row, Col, Tabs, Button, Input, Table, Typography, Space } from "antd";
import { SearchOutlined, UserOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import "./Users.css";

const { Title } = Typography;

interface UserData {
  key: string;
  name: string;
  email: string;
  role: string;
  dateCreated: string;
  lastLogin: string;
}

const Users: React.FC = () => {
  const [activeTab, setActiveTab] = useState("active");
  const [searchTerm, setSearchTerm] = useState("");

  // Sample user data
  const userData: UserData[] = [
    {
      key: "1",
      name: "Jamie Deglado",
      email: "jdeglado@email.com",
      role: "Program Admin",
      dateCreated: "02/14/2025",
      lastLogin: "02/14/2025",
    },
    {
      key: "2",
      name: "Clay Williamson",
      email: "claywill@email.com",
      role: "Program Admin",
      dateCreated: "02/14/2025",
      lastLogin: "02/14/2025",
    },
    {
      key: "3",
      name: "Curtis Ehlow",
      email: "curtis.ehlow@email.com",
      role: "Program Admin",
      dateCreated: "02/01/2025",
      lastLogin: "02/01/2025",
    },
    {
      key: "4",
      name: "Anna Gennette",
      email: "annag@bestpayment.solutions",
      role: "BPS Admin",
      dateCreated: "02/01/2025",
      lastLogin: "02/01/2025",
    },
    {
      key: "5",
      name: "Cedric Rivas",
      email: "cedricr@bestpayment.solutions",
      role: "BPS Admin",
      dateCreated: "02/01/2025",
      lastLogin: "02/01/2025",
    },
    {
      key: "6",
      name: "Riley McDaniels",
      email: "rileym@bestpayment.solutions",
      role: "BPS Support",
      dateCreated: "02/01/2025",
      lastLogin: "02/01/2025",
    },
    {
      key: "7",
      name: "Anna Gennette",
      email: "annag@bestpayment.solutions",
      role: "BPS Admin",
      dateCreated: "01/27/2025",
      lastLogin: "01/27/2025",
    },
    {
      key: "8",
      name: "Curtis Ehlow",
      email: "curtis.ehlow@email.com",
      role: "Program View Only",
      dateCreated: "01/27/2025",
      lastLogin: "01/27/2025",
    },
    {
      key: "9",
      name: "Jamie Deglado",
      email: "jdeglado@email.com",
      role: "Program View Only",
      dateCreated: "01/27/2025",
      lastLogin: "01/27/2025",
    },
    {
      key: "10",
      name: "Cedric Rivas",
      email: "cedricr@bestpayment.solutions",
      role: "Program View Only",
      dateCreated: "01/27/2025",
      lastLogin: "01/27/2025",
    },
  ];

  const columns: ColumnsType<UserData> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: "table-cell-left",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      className: "table-cell-left",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      className: "table-cell-left",
    },
    {
      title: "Date Created",
      dataIndex: "dateCreated",
      key: "dateCreated",
      className: "table-cell-left",
    },
    {
      title: "Last Login",
      dataIndex: "lastLogin",
      key: "lastLogin",
      className: "table-cell-left",
    },
    {
      title: "Actions",
      key: "actions",
      className: "table-cell-left actions-column",
      render: () => (
        <a href="#" className="edit-link">
          Edit
        </a>
      ),
    },
  ];

  // Filter data based on active tab and search term
  const filteredData = userData.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    if (activeTab === "active") {
      // Show all users for "active" tab (since we don't have inactive status in data)
      return matchesSearch;
    } else if (activeTab === "inactive") {
      // No inactive users in our sample data
      return false;
    } else {
      // "all" tab shows everything
      return matchesSearch;
    }
  });

  const activeUserCount = userData.length; // All users are considered active in our sample

  const tabItems = [
    {
      key: "active",
      label: (
        <Space>
          <div className="tab-icon active"></div>
          Active Users
        </Space>
      ),
    },
    {
      key: "inactive",
      label: (
        <Space>
          <div className="tab-icon inactive"></div>
          Inactive
        </Space>
      ),
    },
    {
      key: "all",
      label: (
        <Space>
          <div className="tab-icon all"></div>
          All
        </Space>
      ),
    },
  ];

  return (
    <div className="users-page">
      {/* Page Header */}
      <div className="page-header">
        <Space>
          <img
            src="/images/users-icon-new.svg"
            alt="Users"
            className="page-icon"
          />
          <Title level={3} className="page-title">
            Users
          </Title>
        </Space>
      </div>

      {/* Tabs */}
      <div className="tabs-section">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="users-tabs"
          items={tabItems}
        />
      </div>

      {/* Controls */}
      <Row justify="space-between" align="middle" className="controls-section">
        <Col>
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </Col>
        <Col>
          <Space>
            <div className="user-count">{activeUserCount} Active Users</div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="add-user-btn"
            >
              Add User
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-section">
        <Table
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          className="users-table"
          scroll={{ x: 800 }}
        />
      </div>
    </div>
  );
};

export default Users;
